<template>
  <div class="my-4 w-full mr-16 xs:!mr-0 w-full">
    <!-- Header with actions -->
    <div class="flex justify-between items-center mb-4">
      <div class="flex flex-grow items-center">
        <!-- Search bar (grow but with max width and spacing) -->
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search farms, parcels..."
          class="flex-grow max-w-xl mr-4 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
      </div>
      <div class="flex gap-2 items-center">
        <button
          v-if="canLinkLpis"
          type="button"
          @click="linkLpisParcels"
          class="inline-flex mb-3 justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {{ $root.farmer?.lpis_linked_ids?.length ? "Edit LPIS parcels" : "Link LPIS parcels" }}
        </button>
        <button
          type="button"
          @click="importGeoJSON"
          class="inline-flex mb-3 justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Import Parcels
        </button>
        <button
          type="submit"
          @click="createNewFarm"
          class="inline-flex mb-3 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-[#1E660B] hover:bg-[#1E660B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          New farm +
        </button>
      </div>
    </div>
    
    <!-- Hidden file input -->
    <input
      ref="geoJSONInput"
      type="file"
      accept=".geojson,.json"
      @change="handleGeoJSONUpload"
      style="display: none"
    />

    <!-- Unified Parcels View -->
    <div v-if="view == 'farms'" class="mt-2 pb-2">
      <!-- Bulk Actions Bar -->
      <div v-if="selectedParcels.length > 0" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex items-center justify-between">
          <span class="text-sm text-blue-700 font-medium">
            {{ selectedParcels.length }} parcel(s) selected
          </span>
          <div class="flex gap-2">
            <button
              @click="exportSelected"
              class="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Export
            </button>
            <button
              @click="deleteSelected"
              class="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
            <button
              @click="clearSelection"
              class="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Unified Parcels Table -->
      <div class="bg-white shadow-sm rounded-lg border border-gray-200">
        <!-- Table Header -->
        <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between flex-wrap gap-3">
            <div class="flex items-center gap-4 flex-wrap">
              <button
                @click="toggleAllFarms"
                class="text-sm font-medium text-indigo-600 hover:text-indigo-900"
              >
                {{ allFarmsExpanded ? 'Collapse All' : 'Expand All' }}
              </button>
              <span class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 min-w-[60px]">
                {{ filteredFarmsWithParcels.length }} farm{{ filteredFarmsWithParcels.length !== 1 ? 's' : '' }}
              </span>
              <span class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 min-w-[70px]">
                {{ totalParcelsCount }} parcel{{ totalParcelsCount !== 1 ? 's' : '' }}
              </span>
              <span class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 min-w-[70px]">
                {{ totalHectaresAllFarms }} ha
              </span>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                @click="zoomToExtent"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                title="Zoom map to show all farm parcels"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7V5a2 2 0 012-2h2m8 0h2a2 2 0 012 2v2m0 8v2a2 2 0 01-2 2h-2m-8 0H5a2 2 0 01-2-2v-2M8 12h.01M12 12h.01M16 12h.01" />
                </svg>
                Zoom
              </button>
              <button
                @click="createNewParcel"
                class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                New Parcel
              </button>
            </div>
          </div>
        </div>

        <!-- Table -->
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="w-8 px-2 py-3">
                <input
                  type="checkbox"
                  :checked="allVisibleParcelsSelected"
                  @change="toggleAllParcels"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sortBy('parcelName')">
                Parcel Name
                <span v-if="sortField === 'parcelName'" class="ml-1">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sortBy('fieldNo')">
                Parcel No
                <span v-if="sortField === 'fieldNo'" class="ml-1">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sortBy('crop')">
                Crop
                <span v-if="sortField === 'crop'" class="ml-1">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sortBy('soilType')">
                Soil Type
                <span v-if="sortField === 'soilType'" class="ml-1">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" @click="sortBy('hectare')">
                Area (ha)
                <span v-if="sortField === 'hectare'" class="ml-1">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="farm in filteredFarmsWithParcels" :key="farm.hashid">
              <!-- Farm Header Row -->
              <tr class="bg-gray-50 border-t-2 border-gray-300">
                <td class="px-2 py-3">
                  <input
                    type="checkbox"
                    :checked="allFarmParcelsSelected(farm)"
                    @change="toggleFarmParcels(farm)"
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </td>
                <td colspan="6" class="px-3 py-3">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <button
                        @click="toggleFarmExpansion(farm.hashid)"
                        class="text-gray-400 hover:text-gray-600"
                      >
                        <svg class="h-5 w-5 transition-transform" :class="{ 'transform rotate-90': expandedFarms[farm.hashid] }" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                        </svg>
                      </button>
                      <h4 class="text-lg font-semibold text-gray-900">{{ farm.name }}</h4>
                    </div>
                    <div class="flex items-center gap-3">
                      <button
                        type="button"
                        @click="zoomToFarm(farm)"
                        :title="farm.location ? 'Zoom to farm' : 'Farm location not set'"
                        :disabled="!farm.location"
                        class="disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                      >
                        <LocationMark :colour="farm.location ? '#1E660B' : '#FE2121'" />
                      </button>
                      <span class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 min-w-[80px]">
                        {{ farm.parcels.length }} parcel{{ farm.parcels.length !== 1 ? 's' : '' }}
                      </span>
                      <span class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 min-w-[60px]">
                        {{ parseFloat(getFarmTotalHectares(farm)).toFixed(1) }} ha
                      </span>
                      <button
                        @click="farmEditDirect(farm)"
                        class="text-indigo-600 hover:text-indigo-900 text-sm font-medium ml-2"
                      >
                        Edit Farm
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              
              <!-- Parcel Rows (shown when farm is expanded) -->
              <template v-if="expandedFarms[farm.hashid]">
                <!-- Show message when farm has no parcels -->
                <tr v-if="farm.parcels.length === 0" class="bg-gray-50">
                  <td colspan="7" class="px-3 py-4 text-center text-sm text-gray-500 italic">
                    This farm has no parcels yet. 
                    <button @click="farmEdit(farm)" class="text-indigo-600 hover:text-indigo-900 font-medium">
                      Add parcels
                    </button>
                  </td>
                </tr>
                
                <tr
                  v-for="parcel in farm.parcels"
                  :key="parcel.hashid"
                  class="hover:bg-gray-50"
                >
                  <td class="px-2 py-3">
                    <input
                      type="checkbox"
                      :value="parcel.hashid"
                      v-model="selectedParcels"
                      class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </td>
                  <td class="px-3 py-3 text-sm font-medium text-gray-900">{{ parcel.name }}</td>
                  <td class="px-3 py-3 text-sm text-gray-900">
                    {{ parcel.field_no }}{{ parcel.field_no_suffix ? parcel.field_no_suffix : '' }}
                  </td>
                  <td class="px-3 py-3 text-sm text-gray-900">{{ getCropLabel(parcel.crop) }}</td>
                  <td class="px-3 py-3 text-sm text-gray-900">{{ getSoilTypeLabel(parcel.soil_type) }}</td>
                  <td class="px-3 py-3 text-sm text-gray-900">{{ parcel.hectare ? parseFloat(parcel.hectare).toFixed(2) : (parcel.acre ? (parseFloat(parcel.acre) / 2.471).toFixed(2) : '-') }}</td>
                  <td class="px-3 py-3 text-sm text-gray-500">
                    <div class="flex gap-2">
                      <button
                        @click="editParcel(farm, parcel)"
                        class="text-indigo-600 hover:text-indigo-900 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        @click="deleteParcel(farm, parcel)"
                        class="text-red-600 hover:text-red-900 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="filteredFarmsWithParcels.length === 0" class="text-center py-12">
          <div class="text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No parcels found</h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ searchQuery ? 'Try adjusting your search criteria.' : 'Get started by creating a new farm.' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <FarmWidget
    :farmHashId="farmHashId"
    :showSamplePath="showSamplePath"
    :currentFarm="currentFarm"
    :farmId="farmId"
    :farms="farms"
    :farmer_zones="zones"
    :farmer="farmer"
    v-if="view == 'create'"
    :action="action"
    @changeStage="changeWidgetStage"
    @close="closeCreate"
    :step="step"
    :initialFarmName="pendingFarmName"
  />
</template>

<script>
import { canLinkDafmLpis, showDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import IndexMenuItem from "@/Components/IndexMenuItem";
import FarmWidget from "./FarmWidget";
import { usePage } from "@inertiajs/inertia-vue3";
import LocationMark from "@/Components/Icons/LocationMark.vue";
import axios from "axios";
import ArcgisFeature from "@/Partials/Map/Util/ArcgisFeature";

export default {
  name: "farmComponent",
  components: {
    IndexMenuItem,
    FarmWidget,
    LocationMark,
  },
  props: {
    farmer: Object,
    farms: Array,
    zones: Array,
    showSamplePath: [String, Boolean],
  },
  data() {
    return {
      view: "farms",
      step: 1,
      farmHashId: null,
      farmId: null,
      currentFarm: null,
      action: "create",
      pendingImportData: null, // Store GeoJSON data while setting farm location
      pendingFarmName: null, // Store farm name for import
      // Unified parcels view data
      searchQuery: "",
      selectedParcels: [],
      expandedFarms: {},
      sortField: "parcelName",
      sortDirection: "asc",
      localFarms: [], // Local copy of farms data to avoid prop mutation
      parcelToEdit: null, // Store parcel data for editing
      cameFromUnifiedParcelsView: false, // Track if parcel creation came from unified view
    };
  },
  mounted() {
    this.$root.search.farmer.selected = this.farmer.id;
    // Initialize local farms data
    this.localFarms = [...this.farms];
    // Always load all farms' parcels since we're always in parcels view
    this.loadAllFarmsParcels();
    // Initialize expanded farms - expand all by default
    this.initializeExpandedFarms();
    
    // Check if we should auto-trigger import from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    if (action) {
      // Clear the parameter from URL to prevent re-triggering
      urlParams.delete('action');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
    if (action === 'link-lpis' && this.canLinkLpis) {
      // The map starts linking once the DAFM layer is ready
      this.$nextTick(() => this.linkLpisParcels());
    }
    if (action === 'import') {
      // Small delay to ensure the component is fully mounted
      this.$nextTick(() => {
        setTimeout(() => {
          this.importGeoJSON();
        }, 500);
      });
    }
  },
  computed: {
    canLinkLpis() {
      return canLinkDafmLpis(this.$page.props.config, this.farmer.organisation);
    },
    // Get all farms (both with and without parcels)
    farmsWithParcels() {
      const farmsData = this.localFarms;
      if (!farmsData || !Array.isArray(farmsData)) {
        return [];
      }
      // Include all farms, ensuring each farm has a parcels array (even if empty)
      return farmsData.map(farm => ({
        ...farm,
        name: farm.name || farm.farm_name, // Handle both name and farm_name fields
        parcels: farm.parcels && Array.isArray(farm.parcels) ? farm.parcels : []
      }));
    },

    // Filtered farms based on search query
    filteredFarmsWithParcels() {
      if (!this.searchQuery.trim()) {
        return this.sortedFarms;
      }

      const query = this.searchQuery.toLowerCase();
      return this.sortedFarms.filter(farm => {
        // Search in farm name
        if (farm.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in parcel names, numbers, and suffixes (only if farm has parcels)
        if (farm.parcels && farm.parcels.length > 0) {
          return farm.parcels.some(parcel => {
            const parcelNumber = `${parcel.field_no}${parcel.field_no_suffix || ''}`;
            return (
              parcel.name.toLowerCase().includes(query) ||
              parcelNumber.toLowerCase().includes(query) ||
              (parcel.crop && parcel.crop.toLowerCase().includes(query)) ||
              (parcel.soil_type && parcel.soil_type.toLowerCase().includes(query))
            );
          });
        }
        
        // If no parcels, only match on farm name (already checked above)
        return false;
      });
    },

    // Sorted farms based on sort criteria
    sortedFarms() {
      const farms = [...this.farmsWithParcels];
      
      return farms.sort((a, b) => {
        let valueA, valueB;
        
        switch (this.sortField) {
          case 'parcelName':
            // Sort by first parcel name in farm
            valueA = (a.parcels[0]?.name || '').toLowerCase();
            valueB = (b.parcels[0]?.name || '').toLowerCase();
            break;
          case 'fieldNo':
            // Sort by first parcel field number in farm
            valueA = a.parcels[0]?.field_no || 0;
            valueB = b.parcels[0]?.field_no || 0;
            break;
          case 'crop':
            // Sort by first parcel crop in farm
            valueA = (a.parcels[0]?.crop || '').toLowerCase();
            valueB = (b.parcels[0]?.crop || '').toLowerCase();
            break;
          case 'soilType':
            // Sort by first parcel soil type in farm
            valueA = (a.parcels[0]?.soil_type || '').toLowerCase();
            valueB = (b.parcels[0]?.soil_type || '').toLowerCase();
            break;
          case 'hectare':
            // Sort by total farm area in hectares
            valueA = parseFloat(this.getFarmTotalHectares(a));
            valueB = parseFloat(this.getFarmTotalHectares(b));
            break;
          default:
            return 0;
        }
        
        if (this.sortDirection === 'asc') {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    },

    // Total number of parcels across all farms
    totalParcelsCount() {
      return this.farmsWithParcels.reduce((sum, farm) => sum + (farm.parcels?.length || 0), 0);
    },

    // Total hectares across all farms
    totalHectaresAllFarms() {
      return this.farmsWithParcels.reduce((sum, farm) => {
        const ha = parseFloat(this.getFarmTotalHectares(farm));
        return sum + (isNaN(ha) ? 0 : ha);
      }, 0).toFixed(1);
    },

    // Check if all visible parcels are selected
    allVisibleParcelsSelected() {
      const allVisibleParcels = this.filteredFarmsWithParcels.flatMap(farm => 
        this.expandedFarms[farm.hashid] ? farm.parcels : []
      );
      return allVisibleParcels.length > 0 && 
             allVisibleParcels.every(parcel => this.selectedParcels.includes(parcel.hashid));
    },

    // Check if all farms are expanded
    allFarmsExpanded() {
      return this.filteredFarmsWithParcels.every(farm => this.expandedFarms[farm.hashid]);
    }
  },
  watch: {
    farms: {
      handler(newFarms) {
        if (newFarms && Array.isArray(newFarms)) {
          this.localFarms = [...newFarms];
          this.initializeExpandedFarms();
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    zoomToFarm(farm) {
      if (!farm || !farm.hashid) return;
      // Fetch map data but set active_farm to restrict zoom target
      const farmerId = this.farmer.id || this.farmer.farmer_id;
      if (!farmerId) return;
      axios.post(route("app.farmer.map.view", farmerId), { view: "parcels" })
        .then(response => {
          if (!response.data) return;
          // Enable zoom
          response.data.zoom = true;
          // Set active farm
          response.data.active_farm = farm.hashid;
          // Keep all farms visible; ArcgisFeature will use active_farm to constrain zoom extent.
          this.$root.farmer = response.data;
          // Disable zoom after animation
          setTimeout(() => { if (this.$root.farmer) this.$root.farmer.zoom = false; }, 1200);
        })
        .catch(err => console.error('Zoom to farm failed:', err));
    },
    zoomToExtent() {
      // Replicate initial farmer map load logic: fetch map view with parcels, set zoom=true before assignment.
      // This ensures ArcgisFeature processes graphics with zoom flag active on first render.
      const farmerId = this.farmer.id || this.farmer.farmer_id;
      if (!farmerId) return;
      axios.post(route("app.farmer.map.view", farmerId), { view: "parcels" })
        .then(response => {
          if (!response.data) return;
          // Enable zoom for this render
          response.data.zoom = true;
          this.$root.farmer = response.data;
          // Update local farms + expanded state so unified table stays accurate
          this.localFarms = response.data.farms || [];
          this.initializeExpandedFarms();
          // After a short delay disable zoom to prevent future auto-zoom side effects
          setTimeout(() => {
            if (this.$root.farmer) {
              this.$root.farmer.zoom = false;
            }
          }, 1200);
        })
        .catch(err => {
          console.error('Zoom to extent failed:', err);
          this.$swal && this.$swal.fire('Error', 'Unable to zoom to extent.', 'error');
        });
    },
    // this is been called by child components to refresh this page after updating farm details eg: parcel, zones
    async changeWidgetStage(stage, data = null) {
      //check of the stage is reload to refresh the page
      if (stage == "reload") {
        this.$inertia.reload();
        await this.loadFarmerFarms();
        
        // If we have pending import data, proceed with the import now that farm is created
        if (this.pendingImportData) {
          this.processPendingImport(data?.createdFarm);
        }
      } else if (stage === "farms-refresh") {
        // Refresh data and return to unified view
        this.view = "farms";
        this.loadAllFarmsParcels();
        // Reset unified view flag after explicit refresh
        if (this.cameFromUnifiedParcelsView) {
          this.cameFromUnifiedParcelsView = false;
        }
      } else if (stage === "farms") {
        // Returning to unified parcels view (e.g. cancel from parcel edit)
        this.view = "farms";
        // If we originated from unified view editing flow, we need to restore map features
        if (this.cameFromUnifiedParcelsView) {
          // We intentionally avoid a full inertia reload here because that clears map layers prematurely.
          // Instead we call loadAllFarmsParcels() which fetches fresh farm+parcel data and repopulates
          // the global farmer object used by the map component, preserving visibility of existing parcels.
          // Reload all farms/parcels without a full inertia reload to repopulate map layers
          this.loadAllFarmsParcels();
          this.cameFromUnifiedParcelsView = false;
        }
      } else {
        this.view = stage;
      }
    },

    async processPendingImport(createdFarm = null) {
      if (!this.pendingImportData) return;
      
      try {
        let newlyCreatedFarm = null;
        
        if (createdFarm) {
          // Use the farm data passed from FarmWidget
          console.log('Raw created farm data:', createdFarm);
          
          newlyCreatedFarm = {
            id: createdFarm.id, // Use id field for the farm ID
            farm: createdFarm.id, // Also set farm field (might be needed for validation)
            hashid: createdFarm.hashid,
            farm_id: createdFarm.hashid, // Keep both for compatibility
            farm_name: createdFarm.name || createdFarm.farm_name,
            parcels: [] // New farm, no existing parcels
          };
          console.log('Processed farm data:', newlyCreatedFarm);
        } else {
          // Fallback to searching by name (for backwards compatibility)
          await this.$nextTick(); // Wait for DOM update
          
          // Try to find the farm multiple times with delay (farms might take time to load)
          let attempts = 0;
          const maxAttempts = 10;
          
          while (!newlyCreatedFarm && attempts < maxAttempts) {
            await this.loadFarmerFarms(); // Ensure farms are loaded
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
            
            const foundFarm = this.farms.find(f => f.farm_name === this.pendingFarmName);
            if (foundFarm) {
              newlyCreatedFarm = {
                farm: foundFarm.farm,
                farm_id: foundFarm.farm_id,
                farm_name: foundFarm.farm_name,
                parcels: foundFarm.parcels || []
              };
            }
            attempts++;
            
            if (newlyCreatedFarm) break;
          }
          
          if (!newlyCreatedFarm) {
            throw new Error(`Newly created farm "${this.pendingFarmName}" not found after ${maxAttempts} attempts`);
          }
        }

        // Update selection data with the farm info
        const selectionData = {
          ...this.pendingImportData.selectionData,
          farmOption: 'existing', // Treat as existing since it's now created
          selectedFarmId: newlyCreatedFarm.farm_id,
          farmData: newlyCreatedFarm // Pass the farm data directly
        };

        // Clear any preview parcels from the map before importing
        this.clearPreviewParcels();
        
        // Process the import
        await this.processGeoJSONImport(this.pendingImportData.geoJSONData, selectionData);
        
        // Clear pending data
        this.pendingImportData = null;
        this.pendingFarmName = null;
        
      } catch (error) {
        console.error('Error processing pending import:', error);
        this.$swal.fire('Error', 'Failed to complete import: ' + error.message, 'error');
        
        // Clear pending data on error
        this.pendingImportData = null;
        this.pendingFarmName = null;
      }
    },

    linkLpisParcels() {
      // On phones the map is hidden behind the page until switched to
      this.$root.showMapOnMobile = true;
      this.$emitter.emit("startLpisSelection", { farmerHashid: this.farmer.id });
    },

    //this load farmers farms
    async loadFarmerFarms() {
      let vm = this;
      try {
        const response = await axios.post(route("app.farmer.map.view", this.$root.search.farmer.selected), {
          view: "parcels",
        });
        
        vm.$root.farmer = response.data;
        vm.$root.farmer.hide_farm_location = true;

        //check for organisation with name of ISEC
        if (vm.farmer.organisation == "ISEC") {
          //load LPIS Map Details
          vm.$root.Lpis.source = "isec";
          vm.$root.Lpis.herdNo = vm.farmer.herd_no;
          vm.$root.Lpis.show = "all";
          vm.$root.Lpis.show_zone = false;

          vm.$root.services.dropoff_points = true;
          vm.$root.services.sampler_location = true;
        } else {
          showDafmLpis(vm.$root.Lpis);
        }
        
        return response.data;
      } catch (error) {
        console.error('Error loading farmer farms:', error);
        throw error;
      }
    },
    closeCreate(action) {
      // Check if we were creating a parcel from the unified parcels view
      if (this.cameFromUnifiedParcelsView) {
        // Return to unified parcels view
        this.view = "farms";
        this.step = 1;
        this.cameFromUnifiedParcelsView = false; // Reset flag
        // reload the page - this should refresh the farms prop which will trigger the watcher
        this.$inertia.reload();
      } else {
        // Default behavior - return to farms view
        (this.view = "farms"), (this.step = 1);
        // reload the page - this should refresh the farms prop which will trigger the watcher
        this.$inertia.reload();
      }
    },
    farmEdit(farm) {
      this.currentFarm = farm;
      this.farmId = farm.farm;
      this.farmHashId = farm.farm_id;
      this.step = 2; //switch to edit tab on the farm widget
      this.view = "create";
    },

    farmEditDirect(farm) {
      // Simply map the unified parcels farm structure to match what farmEdit expects
      const mappedFarm = {
        farm: farm.id, // Use the actual database ID (now available from transformer)
        farm_name: farm.name,
        farm_id: farm.hashid, // Hash ID for routing
        location: farm.location,
        location_xy: farm.location_xy,
        hashid: farm.hashid
      };
      
      // Use the exact same logic as the working farmEdit method
      this.currentFarm = mappedFarm;
      this.farmId = mappedFarm.farm; // This will now be the correct database ID (88)
      this.farmHashId = mappedFarm.farm_id; // Hash ID for routing
      this.step = 1; // Go directly to Form Stage 1 to avoid flash
      this.view = "create";
      this.action = "edit-farm-direct"; // Use different action to trigger direct edit
      this.cameFromUnifiedParcelsView = true; // Mark that we came from unified view
    },

    async farmDelete(farm) {
      //confirm if to delete farm
      let vm = this;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            axios
              .delete(route("app.farm.delete", { farm: farm.farm_id }))
              .then(async function (response) {
                vm.$inertia.reload();
                await vm.loadFarmerFarms(); //load farmer farm location after deleting to get a fresh data
                vm.$swal.fire(
                  "Deleted!",
                  "Parcel has been deleted.",
                  "success"
                );
              });
          }
        });
    },

    createNewFarm() {
      this.view = "create";
      this.step = 1;
      this.action = "create-farm";
    },

    async createNewParcel() {
      // Check if there are any farms available
      if (!this.farmsWithParcels || this.farmsWithParcels.length === 0) {
        this.$swal.fire({
          title: 'No Farms Available',
          text: 'You need to create a farm first before adding parcels.',
          icon: 'info',
          confirmButtonText: 'Create Farm',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.createNewFarm();
          }
        });
        return;
      }

      // Create options for farm selection
      const farmOptions = this.farmsWithParcels.map(farm => 
        `<option value="${farm.hashid}">${farm.name}</option>`
      ).join('');

      const { value: selectedFarmHashId } = await this.$swal.fire({
        title: 'Select Farm',
        html: `
          <div class="text-left">
            <label for="farmSelect" class="block text-sm font-medium text-gray-700 mb-2">
              Choose which farm to add the new parcel to:
            </label>
            <select id="farmSelect" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">Select a farm...</option>
              ${farmOptions}
            </select>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const select = document.getElementById('farmSelect');
          if (!select.value) {
            this.$swal.showValidationMessage('Please select a farm');
            return false;
          }
          return select.value;
        }
      });

      if (selectedFarmHashId) {
        // Find the selected farm
        const selectedFarm = this.farmsWithParcels.find(farm => farm.hashid === selectedFarmHashId);
        if (selectedFarm) {
          // Map the unified parcels farm structure to match what farmEdit expects
          const mappedFarm = {
            farm: selectedFarm.id || selectedFarm.farm, // Database ID
            farm_name: selectedFarm.name,
            farm_id: selectedFarm.hashid, // Hash ID for routing
            location: selectedFarm.location,
            location_xy: selectedFarm.location_xy,
            hashid: selectedFarm.hashid
          };
          
          // Transition to farm editing mode - go directly to parcel creation form
          this.currentFarm = mappedFarm;
          this.farmId = mappedFarm.farm;
          this.farmHashId = mappedFarm.farm_id;
          this.step = 3; // Go directly to step 3 (parcel creation form)
          this.view = "create";
          this.action = "create-parcels-direct"; // Set action to trigger direct parcel creation
          this.cameFromUnifiedParcelsView = true; // Mark that we came from unified view
        }
      }
    },

    getCropLabel(cropValue) {
      const cropMappings = {
        'grassland': 'Grassland',
        'arable': 'Arable',
        'other': 'Other'
      };
      return cropMappings[cropValue] || cropValue || '-';
    },

    getSoilTypeLabel(soilTypeValue) {
      const soilTypeMappings = {
        'mineral_soil': 'Mineral Soil',
        'peat': 'Peat'
      };
      return soilTypeMappings[soilTypeValue] || soilTypeValue || '-';
    },

    getFarmTotalHectares(farm) {
      if (!farm.parcels || farm.parcels.length === 0) return '0.00';
      
      const totalHectares = farm.parcels.reduce((sum, parcel) => {
        let hectares = 0;
        if (parcel.hectare) {
          hectares = parseFloat(parcel.hectare);
        } else if (parcel.acre) {
          // Convert acres to hectares (1 acre = 0.404686 hectares)
          hectares = parseFloat(parcel.acre) / 2.471;
        }
        return sum + hectares;
      }, 0);
      
      return totalHectares.toFixed(2);
    },

    importGeoJSON() {
      this.$refs.geoJSONInput.click();
    },

    async handleGeoJSONUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const geoJSONText = await this.readFile(file);
        const geoJSONData = JSON.parse(geoJSONText);
        
        // Validate GeoJSON structure
        if (!this.validateGeoJSON(geoJSONData)) {
          this.$swal.fire('Error', 'Invalid GeoJSON format', 'error');
          return;
        }

        // Show preview/confirmation dialog
        this.showGeoJSONPreview(geoJSONData);
        
      } catch (error) {
        console.error('Error processing GeoJSON:', error);
        this.$swal.fire('Error', 'Failed to process GeoJSON file', 'error');
      }

      // Reset file input
      event.target.value = '';
    },

    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },

    validateGeoJSON(data) {
      // Basic GeoJSON validation
      return data && 
             data.type && 
             (data.type === 'FeatureCollection' || data.type === 'Feature') &&
             (data.features || data.geometry);
    },

    // Unified parcels view methods
    initializeExpandedFarms() {
      // Expand all farms by default
      this.expandedFarms = {};
      const farmsData = this.localFarms;
      if (farmsData && Array.isArray(farmsData)) {
        farmsData.forEach(farm => {
          if (farm.parcels && Array.isArray(farm.parcels) && farm.parcels.length > 0) {
            this.expandedFarms[farm.hashid || farm.farm_id] = true;
          }
        });
      }
    },

    loadAllFarmsParcels() {
      // Load parcels for all farms
      let vm = this;
      axios
        .post(route("app.farmer.map.view", this.$root.search.farmer.selected), {
          view: "parcels",
        })
        .then(function (response) {
          // Update local farms with parcels data
          vm.localFarms = response.data.farms || [];
          vm.initializeExpandedFarms();
          
          // Also update the global farmer object to refresh the map
          if (response.data) {
            // Ensure zoom is disabled when refreshing after parcel edit
            response.data.zoom = false;
            vm.$root.farmer = response.data;
            
          }
        })
        .catch(function (error) {
          console.error('Error loading parcels:', error);
        });
    },

    toggleFarmExpansion(farmHashId) {
      this.expandedFarms[farmHashId] = !this.expandedFarms[farmHashId];
    },

    toggleAllFarms() {
      const shouldExpand = !this.allFarmsExpanded;
      this.filteredFarmsWithParcels.forEach(farm => {
        this.expandedFarms[farm.hashid] = shouldExpand;
      });
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    toggleAllParcels() {
      const allVisibleParcels = this.filteredFarmsWithParcels.flatMap(farm => 
        this.expandedFarms[farm.hashid] ? farm.parcels : []
      );
      
      if (this.allVisibleParcelsSelected) {
        // Deselect all
        this.selectedParcels = this.selectedParcels.filter(id => 
          !allVisibleParcels.some(parcel => parcel.hashid === id)
        );
      } else {
        // Select all
        const newSelections = allVisibleParcels
          .map(parcel => parcel.hashid)
          .filter(id => !this.selectedParcels.includes(id));
        this.selectedParcels.push(...newSelections);
      }
    },

    allFarmParcelsSelected(farm) {
      return farm.parcels.length > 0 && 
             farm.parcels.every(parcel => this.selectedParcels.includes(parcel.hashid));
    },

    toggleFarmParcels(farm) {
      if (this.allFarmParcelsSelected(farm)) {
        // Deselect all parcels in this farm
        this.selectedParcels = this.selectedParcels.filter(id => 
          !farm.parcels.some(parcel => parcel.hashid === id)
        );
      } else {
        // Select all parcels in this farm
        const newSelections = farm.parcels
          .map(parcel => parcel.hashid)
          .filter(id => !this.selectedParcels.includes(id));
        this.selectedParcels.push(...newSelections);
      }
    },

    clearSelection() {
      this.selectedParcels = [];
    },

    editParcel(farm, parcel) {
      // Set up the farm context for FarmWidget  
      this.currentFarm = farm;
      this.farmId = farm.id; // Use actual database ID (now available from transformer)
      this.farmHashId = farm.hashid;
      this.step = 2; // Go to Step 2 first to load parcels properly
      this.view = "create";
      this.action = "edit-parcel-from-unified";
      this.cameFromUnifiedParcelsView = true; // Mark that we came from unified view
      
      // Store the target parcel info for after parcels are loaded
      this.targetParcel = {
        item_id: parcel.item_id,
        hashid: parcel.hashid
      };
    },

    deleteParcel(farm, parcel) {
      this.$swal
        .fire({
          title: "Are you sure?",
          text: `Delete parcel "${parcel.name}" from ${farm.name}?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            // Call API to delete parcel
            // This would need to be implemented based on your existing parcel deletion logic
            this.performParcelDeletion(parcel);
          }
        });
    },

    async performParcelDeletion(parcel) {
      try {
        // Use the same deletion API as the FarmWidget
        await axios.delete(
          route("app.farm.widget.delete", {
            action: "parcels",
            hashid: parcel.hashid,
          })
        );
        
        // Reload the parcels data after successful deletion
        await this.loadAllFarmsParcels();
        this.$swal.fire("Deleted!", "Parcel has been deleted.", "success");
      } catch (error) {
        console.error('Error deleting parcel:', error);
        this.$swal.fire("Error", "Failed to delete parcel. Please try again.", "error");
      }
    },

    exportSelected() {
      if (this.selectedParcels.length === 0) {
        this.$swal.fire("No Selection", "Please select parcels to export.", "info");
        return;
      }
      // Implement export functionality
      
      this.$swal.fire("Export", "Feature coming soon!", "info");
    },

    deleteSelected() {
      if (this.selectedParcels.length === 0) {
        this.$swal.fire("No Selection", "Please select parcels to delete.", "info");
        return;
      }

      this.$swal
        .fire({
          title: "Are you sure?",
          text: `Delete ${this.selectedParcels.length} selected parcel(s)?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete them!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              // Show loading state
              this.$swal.fire({
                title: 'Deleting parcels...',
                text: `Deleting ${this.selectedParcels.length} parcel(s)`,
                allowOutsideClick: false,
                didOpen: () => {
                  this.$swal.showLoading();
                }
              });

              // Delete each selected parcel
              const deletePromises = this.selectedParcels.map(async (parcelHashId) => {
                try {
                  await axios.delete(
                    route("app.farm.widget.delete", {
                      action: "parcels",
                      hashid: parcelHashId,
                    })
                  );
                  return { success: true, hashid: parcelHashId };
                } catch (error) {
                  console.error(`Error deleting parcel ${parcelHashId}:`, error);
                  return { success: false, hashid: parcelHashId, error };
                }
              });

              const results = await Promise.all(deletePromises);
              const successful = results.filter(r => r.success);
              const failed = results.filter(r => !r.success);

              // Clear selection and reload data
              this.selectedParcels = [];
              await this.loadAllFarmsParcels();

              // Show results
              if (failed.length === 0) {
                this.$swal.fire("Deleted!", `Successfully deleted ${successful.length} parcel(s).`, "success");
              } else if (successful.length === 0) {
                this.$swal.fire("Error", "Failed to delete any parcels. Please try again.", "error");
              } else {
                this.$swal.fire("Partial Success", 
                  `Successfully deleted ${successful.length} parcel(s). Failed to delete ${failed.length} parcel(s).`, 
                  "warning");
              }
            } catch (error) {
              console.error('Error in bulk deletion:', error);
              this.$swal.fire("Error", "An error occurred while deleting parcels. Please try again.", "error");
            }
          }
        });
    },

    async showGeoJSONPreview(geoJSONData) {
      const features = geoJSONData.type === 'FeatureCollection' 
        ? geoJSONData.features 
        : [geoJSONData];
      
      const parcelCount = features.length;
      
      // Create parcel data with all information for the table
      const parcelData = features.map((f, i) => {
        return {
          name: this.cleanParcelName(f.properties?.Name) || 'Parcel ' + (i + 1),
          plotId: f.properties?.PlotId || (i + 1),
          area: f.properties?.GrossArea || 0,
          refArea: f.properties?.RefArea || f.properties?.ReferenceArea || 0,
          crop: f.properties?.CropName || 'Unknown',
          selected: true, // Default to selected
          feature: f
        };
      });

      const tableHTML = this.generateParcelSelectionTable(parcelData);
      const farmOptionsHTML = this.generateFarmOptionsHTML(geoJSONData);
      const farmerName = this.farmer.farmer_name || this.farmer.name || 'this farmer';

      const result = await this.$swal.fire({
        title: 'Import Parcels',
        html: '<div class="text-left">' +
              '<div class="mb-4">' +
              '<p><strong>Found ' + parcelCount + ' parcel(s) for farmer: ' + farmerName + '</strong></p>' +
              '<p class="text-sm text-gray-600 mt-1">Select which parcels to import:</p>' +
              '</div>' +
              tableHTML +
              '<div class="mt-4">' +
              '<h4 class="font-medium mb-2">Import to:</h4>' +
              farmOptionsHTML +
              '</div>' +
              '</div>',
        showCancelButton: true,
        confirmButtonText: 'Import Selected',
        cancelButtonText: 'Cancel',
        width: '900px',
        didOpen: () => {
          // Add select all functionality
          const selectAllCheckbox = document.getElementById('selectAllParcels');
          const parcelCheckboxes = document.querySelectorAll('input[type="checkbox"][data-index]');
          
          if (selectAllCheckbox) {
            selectAllCheckbox.addEventListener('change', function() {
              parcelCheckboxes.forEach(checkbox => {
                checkbox.checked = this.checked;
              });
            });
          }
          
          // Update select all checkbox when individual checkboxes change
          parcelCheckboxes.forEach(checkbox => {
            checkbox.addEventListener('change', function() {
              const allChecked = Array.from(parcelCheckboxes).every(cb => cb.checked);
              const someChecked = Array.from(parcelCheckboxes).some(cb => cb.checked);
              selectAllCheckbox.checked = allChecked;
              selectAllCheckbox.indeterminate = someChecked && !allChecked;
            });
          });
        },
        preConfirm: () => {
          // Get selected parcels
          const checkboxes = document.querySelectorAll('input[type="checkbox"][data-index]');
          const selectedParcels = [];
          checkboxes.forEach((checkbox) => {
            if (checkbox.checked) {
              const index = parseInt(checkbox.getAttribute('data-index'));
              selectedParcels.push(parcelData[index]);
            }
          });
          
          if (selectedParcels.length === 0) {
            this.$swal.showValidationMessage('Please select at least one parcel to import');
            return false;
          }

          // Get farm selection
          const farmOption = document.querySelector('input[name="farmOption"]:checked');
          if (!farmOption) {
            this.$swal.showValidationMessage('Please select a farm option');
            return false;
          }

          let selectedFarmId = null;
          let newFarmName = null;

          if (farmOption.value === 'new') {
            // No validation needed for farm name - it will be set during farm creation
            newFarmName = this.extractFarmName(geoJSONData); // Use default name as fallback
          } else if (farmOption.value === 'existing') {
            const farmSelect = document.getElementById('farmSelect');
            selectedFarmId = farmSelect.value;
            if (!selectedFarmId) {
              this.$swal.showValidationMessage('Please select an existing farm');
              return false;
            }
          }
          
          return {
            parcels: selectedParcels,
            farmOption: farmOption.value,
            selectedFarmId: selectedFarmId,
            newFarmName: newFarmName
          };
        }
      });

      if (result.isConfirmed && result.value) {
        if (result.value.farmOption === 'new') {
          // For new farms, we need to get the location first before creating anything
          this.promptForFarmLocation(geoJSONData, result.value);
        } else {
          // For existing farms, proceed with import directly
          this.processGeoJSONImport(geoJSONData, result.value);
        }
      }
    },

    generateParcelSelectionTable(parcelData) {
      let tableRows = '';
      parcelData.forEach((parcel, index) => {
        tableRows += '<tr class="hover:bg-gray-50">' +
                    '<td class="px-3 py-2">' +
                    '<input type="checkbox" checked data-index="' + index + '">' +
                    '</td>' +
                    '<td class="px-3 py-2 font-medium">' + parcel.name + '</td>' +
                    '<td class="px-3 py-2">' + parcel.plotId + '</td>' +
                    '<td class="px-3 py-2">' + parcel.area.toFixed(2) + '</td>' +
                    '<td class="px-3 py-2">' + parcel.refArea.toFixed(2) + '</td>' +
                    '<td class="px-3 py-2">' + parcel.crop + '</td>' +
                    '</tr>';
      });

      return '<div class="max-h-96 overflow-y-auto border border-gray-300 rounded">' +
             '<table class="min-w-full divide-y divide-gray-200 text-sm">' +
             '<thead class="bg-gray-50 sticky top-0">' +
             '<tr>' +
             '<th class="px-3 py-2 text-left">' +
             '<input type="checkbox" id="selectAllParcels" checked>' +
             '</th>' +
             '<th class="px-3 py-2 text-left font-medium">Name</th>' +
             '<th class="px-3 py-2 text-left font-medium">NMP Plot ID</th>' +
             '<th class="px-3 py-2 text-left font-medium">Area (ha)</th>' +
             '<th class="px-3 py-2 text-left font-medium">Ref Area (ha)</th>' +
             '<th class="px-3 py-2 text-left font-medium">Crop</th>' +
             '</tr>' +
             '</thead>' +
             '<tbody class="bg-white divide-y divide-gray-200">' +
             tableRows +
             '</tbody>' +
             '</table>' +
             '</div>';
    },

    generateFarmOptionsHTML(geoJSONData) {
      let farmOptions = '<div class="space-y-3">';
      
      // Option to create new farm (simplified - no name input here)
      farmOptions += '<div class="flex items-center">' +
                    '<input type="radio" id="newFarm" name="farmOption" value="new" checked class="mr-2">' +
                    '<label for="newFarm" class="mr-2">Create new farm </label>' +
                    '</div>';

      // Options for existing farms
      if (this.farms && this.farms.length > 0) {
        farmOptions += '<div class="flex items-center mt-3">' +
                      '<input type="radio" id="existingFarm" name="farmOption" value="existing" class="mr-2">' +
                      '<label for="existingFarm" class="mr-2">Add to existing farm:</label>' +
                      '<select id="farmSelect" class="border border-gray-300 rounded px-2 py-1 text-sm flex-1">' +
                      '<option value="">Select a farm...</option>';
        
        this.farms.forEach((farm) => {
          farmOptions += '<option value="' + farm.farm_id + '">' + farm.farm_name + ' (' + farm.parcel_count + ' parcels)</option>';
        });
        
        farmOptions += '</select>' +
                      '</div>';
      }

      farmOptions += '</div>';
      return farmOptions;
    },

    async promptForFarmLocation(geoJSONData, selectionData) {
      // Store the import data for later use
      this.pendingImportData = {
        geoJSONData: geoJSONData,
        selectionData: selectionData
      };
      
      // Automatically preview the parcels on the map to help with farm location setting
      await this.previewParcelsOnMap(geoJSONData, selectionData);
      
      // Switch to farm creation view to set location
      this.createNewFarmForImport();
    },

    async previewParcelsOnMap(geoJSONData, selectionData) {
      try {
        // Convert GeoJSON features to the format expected by the map
        const features = geoJSONData.type === 'FeatureCollection' 
          ? geoJSONData.features 
          : [geoJSONData];
          
        // Filter to only selected parcels if specified
        const featuresToPreview = selectionData.parcels 
          ? selectionData.parcels.map(p => p.feature)
          : features;

        // Create preview parcel data in the same format as existing parcels
        const previewParcels = featuresToPreview.map((feature, index) => {
          const properties = feature.properties || {};
          
          // Extract coordinates properly from GeoJSON
          // GeoJSON Polygon coordinates are [[[lng,lat],[lng,lat],...]] but we need [[lng,lat],[lng,lat],...]
          let coordinates = feature.geometry.coordinates;
          if (feature.geometry.type === 'Polygon' && coordinates && coordinates[0]) {
            coordinates = coordinates[0]; // Get the outer ring
          }
          
          // Validate coordinates format - ensure we have proper [lng, lat] pairs
          if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) {
            console.warn(`Invalid coordinates for preview parcel ${index + 1}:`, coordinates);
            return null; // Skip invalid geometries
          }
          
          // Ensure all coordinate pairs are valid numbers
          const validCoordinates = coordinates.filter(coord => {
            return Array.isArray(coord) && coord.length === 2 && 
                   typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
                   !isNaN(coord[0]) && !isNaN(coord[1]);
          });
          
          if (validCoordinates.length < 3) {
            console.warn(`Insufficient valid coordinates for preview parcel ${index + 1}`);
            return null; // Skip if not enough valid coordinates for a polygon
          }
          
          return {
            item_id: `preview_${index + 1}`,
            hashid: `preview_${index + 1}`, 
            name: this.cleanParcelName(properties.Name) || `Parcel ${index + 1}`,
            field_no: String(index + 1),
            field_no_suffix: null,
            crop: this.mapNMPCropToYourSystem(properties.CropName),
            soil_type: "mineral_soil",
            acre: this.convertHectaresToAcres(properties.GrossArea || 0),
            identifier: null,
            identifier_xy: null,
            boundaries: validCoordinates,
            boundaries_xy: validCoordinates, // Will be converted by map component
            arcgis_object_id: null,
            arcgis_global_id: null
          };
        }).filter(parcel => parcel !== null); // Remove invalid parcels

        // Create preview farmer data structure for the map
        // IMPORTANT: ArcGIS zoom conditions require:
        // (!farmer.is_ISEC || (farmer.is_ISEC && farmer.herd_no === null) || (check_if_one_farm_have_location && !check_if_no_farm_have_parcels))
        // AND farmer?.zoom must be true
        const previewFarmerData = {
          name: this.farmer.name,
          hashid: this.farmer.id,
          herd_no: null, // Must be null to satisfy zoom conditions
          is_ISEC: false, // Must be false to satisfy zoom conditions  
          zoom: true, // Enable zooming to show all parcels
          hide_farm_location: true, // Hide farm location since we haven't set it yet
          allowToLocationOnMap: false, // Prevent location setting during preview
          farms: [{
            item_id: 'preview_farm',
            hashid: 'preview_farm_hash',
            name: 'Preview - Imported Parcels',
            location: null, // Must be null, not empty array
            location_xy: null, // Must be null, not empty array
            parcels: previewParcels,
            sample_zones: [] // Required by ArcGIS processing
          }]
        };

        // Display the preview data on the map
        this.$root.farmer = previewFarmerData;
        
        // Give the map system time to process and complete the zoom animation (increased delay)
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Show a notification that this is a preview with a less intrusive toast-style approach
        this.$swal.fire({
          title: 'Parcels Preview',
          html: `
            <div class="text-left">
              <p class="text-sm text-gray-600">
                <strong>${previewParcels.length} parcels</strong> from your GeoJSON file are displayed on the map.
              </p>
              <p class="text-sm text-gray-600 mt-2">
                Next, you'll create a farm by setting the farm name and clicking on the map to set the location.
              </p>
              <div class="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                <p class="text-xs text-blue-800">
                  These parcels will be imported after you create the farm.
                </p>
              </div>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'Got it',
          width: '450px',
          position: 'top-end',
          toast: true,
          timer: 8000,
          timerProgressBar: true,
          showCloseButton: true,
          didOpen: () => {
            // Make the toast less intrusive by reducing opacity slightly
            const popup = this.$swal.getPopup();
            if (popup) {
              popup.style.opacity = '0.95';
            }
          }
        });

      } catch (error) {
        console.error('Error previewing parcels on map:', error);
        this.$swal.fire('Error', 'Failed to preview parcels on map', 'error');
      }
    },

    clearPreviewParcels() {
      // Clear any preview data from the map by resetting the farmer data
      // This will be replaced with actual farm data after import
      if (this.$root.farmer && this.$root.farmer.farms) {
        // Check if we have preview data (farms with preview_farm id)
        const hasPreviewData = this.$root.farmer.farms.some(farm => farm.item_id === 'preview_farm');
        if (hasPreviewData) {
          // Clear the preview by setting to null, will be replaced by actual data
          this.$root.farmer = null;
        }
      }
    },

    createNewFarmForImport() {
      // Set up farm creation using standard create-farm action
      // Farm name will be set by user during farm creation
      this.step = 1;
      this.action = "create-farm";
      this.pendingFarmName = null; // Don't pre-fill the name
      this.view = "create";
    },

    async processGeoJSONImport(geoJSONData, selectionData) {
      try {
        // Show loading
        this.$swal.fire({
          title: 'Importing...',
          text: 'Creating parcels from GeoJSON data',
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        let farmData;
        
        if (selectionData.farmOption === 'new') {
          // This should not happen anymore as new farms go through location setting first
          throw new Error('New farm creation should go through location setting first');
        } else {
          // Check if farm data is passed directly (for newly created farms)
          if (selectionData.farmData) {
            farmData = selectionData.farmData;
            console.log('Using passed farm data:', farmData);
          } else {
            // Use existing farm from farms list
            const existingFarm = this.farms.find(f => f.farm_id === selectionData.selectedFarmId);
            if (!existingFarm) {
              throw new Error('Selected farm not found');
            }
            farmData = {
              id: existingFarm.farm,
              hashid: existingFarm.farm_id,
              farm_name: existingFarm.farm_name,
              parcels: existingFarm.parcels || [] // Include existing parcels for field number checking
            };
          }
        }
        
        // Create parcels using existing process
        const results = await this.createParcelsFromGeoJSON(geoJSONData, farmData, selectionData.parcels);

        console.log('Import results:', results);
        console.log('Parcels processed:', results.parcelCount);
        console.log('Expected parcels:', selectionData.parcels.length);

        const actionText = selectionData.farmOption === 'new' ? 'Import completed - Farm created with parcels' : 'Parcels added to existing farm';
        const locationPrompt = ''; // No longer needed since location is set during farm creation

        const result = await this.$swal.fire({
          title: 'Import Status', 
          html: `
            <div class="text-left">
              <p><strong>${actionText}:</strong></p>
              <ul class="list-disc list-inside mt-2">
                <li>Farm: ${farmData.farm_name}</li>
                <li>Processed: ${results.parcelCount} of ${selectionData.parcels.length} parcels</li>
                <li>Zones: ${results.zoneCount} created</li>
                ${locationPrompt}
              </ul>
              ${results.parcelCount < selectionData.parcels.length ? '<p class="mt-2 text-orange-600"><strong>Warning:</strong> Some parcels may not have been created. Check console for details.</p>' : ''}
            </div>
          `,
          icon: results.parcelCount === selectionData.parcels.length ? 'success' : 'warning',
          confirmButtonText: 'OK'
        });

        // Wait a moment to ensure backend processing is complete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        console.log('Refreshing map and farms data after import...');
        
        // Navigate back to farms list view after successful import
        this.view = "farms";
        this.currentFarm = null;
        this.step = 1;
        this.action = null;
        
        // Refresh the map data to show imported parcels immediately with zoom to extent
        const farmerId = this.farmer.id || this.farmer.farmer_id;
        if (farmerId) {
          try {
            const response = await axios.post(route("app.farmer.map.view", farmerId), { view: "parcels" });
            if (response.data) {
              // Enable zoom to show all parcels including newly imported ones
              response.data.zoom = true;
              this.$root.farmer = response.data;
              
              // Update local farms data for the unified table
              this.localFarms = response.data.farms || [];
              this.initializeExpandedFarms();
              
              console.log('Map refreshed with zoom to show imported parcels');
              
              // Disable zoom after animation completes
              setTimeout(() => {
                if (this.$root.farmer) {
                  this.$root.farmer.zoom = false;
                }
              }, 1200);
            }
          } catch (error) {
            console.error('Error refreshing map after import:', error);
            // Fallback to regular refresh without zoom
            await this.loadAllFarmsParcels();
          }
        } else {
          // Fallback if farmer ID not available
          await this.loadAllFarmsParcels();
        }
        
        // Give the map component time to process the new data and zoom animation
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Also reload the page to ensure all data is fresh
        this.$inertia.reload();

      } catch (error) {
        console.error('Import error:', error);
        console.error('Error details:', error.response?.data);
        let errorMessage = 'Failed to import GeoJSON: ' + (error.response?.data?.message || error.message);
        
        // If it's a validation error, show the specific fields that failed
        if (error.response?.status === 422 && error.response?.data?.errors) {
          const validationErrors = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          errorMessage += '\n\nValidation errors:\n' + validationErrors;
        }
        
        this.$swal.fire('Error', errorMessage, 'error');
      }
    },

    async processPendingImport(createdFarm = null) {
      if (!this.pendingImportData) return;
      
      try {
        let newlyCreatedFarm = null;
        
        if (createdFarm) {
          // Use the farm data passed from FarmWidget
          console.log('Raw created farm data:', createdFarm);
          
          newlyCreatedFarm = {
            id: createdFarm.id, // Use id field for the farm ID
            farm: createdFarm.id, // Also set farm field (might be needed for validation)
            hashid: createdFarm.hashid,
            farm_id: createdFarm.hashid, // Keep both for compatibility
            farm_name: createdFarm.name || createdFarm.farm_name,
            parcels: [] // New farm, no existing parcels
          };
          console.log('Processed farm data:', newlyCreatedFarm);
        } else {
          // Fallback to searching by name (for backwards compatibility)
          await this.$nextTick(); // Wait for DOM update
          
          // Try to find the farm multiple times with delay (farms might take time to load)
          let attempts = 0;
          const maxAttempts = 10;
          
          while (!newlyCreatedFarm && attempts < maxAttempts) {
            await this.loadFarmerFarms(); // Ensure farms are loaded
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
            
            const foundFarm = this.farms.find(f => f.farm_name === this.pendingFarmName);
            if (foundFarm) {
              newlyCreatedFarm = {
                farm: foundFarm.farm,
                farm_id: foundFarm.farm_id,
                farm_name: foundFarm.farm_name,
                parcels: foundFarm.parcels || []
              };
            }
            attempts++;
            
            if (newlyCreatedFarm) break;
          }
          
          if (!newlyCreatedFarm) {
            throw new Error(`Newly created farm "${this.pendingFarmName}" not found after ${maxAttempts} attempts`);
          }
        }

        // Update selection data with the farm info
        const selectionData = {
          ...this.pendingImportData.selectionData,
          farmOption: 'existing', // Treat as existing since it's now created
          selectedFarmId: newlyCreatedFarm.farm_id,
          farmData: newlyCreatedFarm // Pass the farm data directly
        };

        // Clear any preview parcels from the map before importing
        this.clearPreviewParcels();
        
        // Process the import
        await this.processGeoJSONImport(this.pendingImportData.geoJSONData, selectionData);
        
        // Clear pending data
        this.pendingImportData = null;
        this.pendingFarmName = null;
        
      } catch (error) {
        console.error('Error processing pending import:', error);
        this.$swal.fire('Error', 'Failed to complete import: ' + error.message, 'error');
        
        // Clear pending data on error
        this.pendingImportData = null;
        this.pendingFarmName = null;
      }
    },

    async createParcelsFromGeoJSON(geoJSONData, farm, selectedParcels = null) {
      // If selectedParcels is provided, use only those features
      const featuresToProcess = selectedParcels 
        ? selectedParcels.map(p => p.feature)
        : (geoJSONData.type === 'FeatureCollection' ? geoJSONData.features : [geoJSONData]);

      let parcelCount = 0;
      let zoneCount = 0;
      
      // Track field numbers used during this import to avoid duplicates
      const usedFieldNos = new Set();
      
      // Add existing field numbers to the tracking set
      if (farm.parcels && Array.isArray(farm.parcels)) {
        farm.parcels.forEach(parcel => {
          if (parcel.fieldNo || parcel.field_no) {
            usedFieldNos.add(parseInt(parcel.fieldNo || parcel.field_no));
          }
        });
      }

      console.log(`Starting to process ${featuresToProcess.length} features`);
      console.log(`Farm data:`, farm);
      console.log(`Farm parcels:`, farm.parcels);
      console.log(`Existing field numbers in farm:`, Array.from(usedFieldNos));
      
      for (const [index, feature] of featuresToProcess.entries()) {
        console.log(`Processing feature ${index + 1}/${featuresToProcess.length}`);
        
        if (!feature.geometry || !['Polygon', 'MultiPolygon'].includes(feature.geometry.type)) {
          console.log(`Skipping feature ${index + 1} - invalid geometry`);
          continue;
        }

        const properties = feature.properties;
        
        // Prepare parcel data using existing structure
        // Note: We send the raw GeoJSON coordinates and let the backend handle the conversion
        // Generate sequential field number (standard Livefarm approach)
        let uniqueFieldNo = 1;
        while (usedFieldNos.has(uniqueFieldNo)) {
          uniqueFieldNo++;
        }
        
        // Mark this field number as used
        usedFieldNos.add(uniqueFieldNo);
        
        console.log(`Assigning field number ${uniqueFieldNo} to parcel "${properties.Name || 'Unnamed'}"`);
        console.log(`NMP Plot ID: ${properties.PlotId || 'None'}`);
        console.log(`Using farm ID: ${farm.id || farm.farm} (from farm.id: ${farm.id}, farm.farm: ${farm.farm})`);
        
        const parcelData = {
          farm: farm.id || farm.farm, // Try both id and farm fields
          name: this.cleanParcelName(properties.Name) || `Parcel ${uniqueFieldNo}`,
          fieldNo: String(uniqueFieldNo), // Convert to string for validation
          field_no_suffix: null, // Explicitly set as null for imports
          nmp_plot_id: properties.PlotId || null, // Store NMP Plot ID in separate field
          crop: this.mapNMPCropToYourSystem(properties.CropName),
          soilType: "mineral_soil", // Default, could be enhanced
          createSampleZone: true, // Use existing checkbox functionality
          boundaries: this.ensureCounterClockwiseWinding(feature.geometry.coordinates), // EPSG:4326 coordinates with correct winding
          // Don't set boundaries_xy here - let backend convert it
          acre: this.convertHectaresToAcres(properties.GrossArea || 0),
          hectare: properties.GrossArea || 0,
          farmer: {
            farmer_id: parseInt(this.farmer.farmer_id) // Use farmer_id property for the numeric ID
          },
          type: "parcels",
          action: "creating", // Required field for backend validation
          convert_coordinates: true // Flag to tell backend to convert coordinates
        };

        try {
          // Debug: Log the farmer ID we're sending
          console.log('Farmer ID being sent:', this.farmer.farmer_id, 'Type:', typeof this.farmer.farmer_id);
          console.log('Parsed farmer ID:', parseInt(this.farmer.farmer_id));
          console.log('Full parcel data:', parcelData);
          
          // Use existing parcel creation endpoint
          const response = await axios.post(
            route("app.farm.storeFarmParcelsAndZones", farm.hashid), 
            parcelData
          );

          console.log(`Parcel "${parcelData.name}" created successfully`);
          console.log('Parcel creation response:', response.data);
          
          parcelCount++;
          if (parcelData.createSampleZone) {
            zoneCount++;
          }
          
          console.log(`Current counts: ${parcelCount} parcels, ${zoneCount} zones`);
        } catch (parcelError) {
          console.error(`Error creating parcel ${index + 1}:`, parcelError);
          console.error('Parcel data that failed:', parcelData);
          console.error('Error response:', parcelError.response);
          
          let errorMessage = `Failed to create parcel "${parcelData.name}"`;
          
          if (parcelError.response?.status === 422) {
            console.error('422 Validation errors:', parcelError.response.data.errors);
            console.error('422 Response data:', parcelError.response.data);
            
            // Add detailed validation errors to the error message
            if (parcelError.response?.data?.errors) {
              const validationErrors = Object.entries(parcelError.response.data.errors)
                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                .join('; ');
              errorMessage += ` (Validation: ${validationErrors})`;
            }
          } else if (parcelError.response?.status === 500) {
            errorMessage += ' (Server Error - check console for details)';
          }
          
          if (parcelError.response?.data?.message) {
            console.error('Backend message:', parcelError.response.data.message);
            errorMessage += `: ${parcelError.response.data.message}`;
          } else {
            errorMessage += `: ${parcelError.message}`;
          }
          
          throw new Error(errorMessage);
        }
      }

      return { parcelCount, zoneCount };
    },

    mapNMPCropToYourSystem(nmpCrop) {
      // Map NMP crop types to existing crop values
      const cropMapping = {
        'Grazing': 'grassland',
        'Grassland': 'grassland',
        'Cereals': 'cereal_maize_osr',
        'Maize': 'cereal_maize_osr',
        'OSR': 'cereal_maize_osr',
        'Beet': 'beet_peas_beans',
        'Peas': 'beet_peas_beans',
        'Beans': 'beet_peas_beans',
        'Potatoes': 'potatoes_oats',
        'Oats': 'potatoes_oats'
      };

      return cropMapping[nmpCrop] || 'grassland'; // Default to grassland
    },

    convertHectaresToAcres(hectares) {
      return hectares * 2.47105; // 1 hectare = 2.47105 acres
    },

    // Helper function to ensure polygon rings have correct counter-clockwise winding
    ensureCounterClockwiseWinding(coordinates) {
      if (!coordinates || !Array.isArray(coordinates)) {
        return coordinates;
      }
      
      // For Polygon geometry: coordinates[0] is the exterior ring
      if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0]) && coordinates[0][0].length === 2) {
        // This is a Polygon with coordinates = [[ring1], [ring2], ...]
        // Use the ArcgisFeature utility which has access to geodesicUtils
        const fixedCoordinates = ArcgisFeature.fixPolygonWinding(coordinates);
        return fixedCoordinates;
      }
      
      // For MultiPolygon or other formats, handle recursively
      return coordinates.map(coord => this.ensureCounterClockwiseWinding(coord));
    },

    cleanParcelName(name) {
      if (!name) return null;
      
      // Remove area information in parentheses (e.g., "(2.51 ha)", "(1.23 hectares)", etc.)
      return name.replace(/\s*\([^)]*\bha?\b[^)]*\)/gi, '').trim();
    },

    extractFarmName(geoJSONData) {
      const firstFeature = geoJSONData.features && geoJSONData.features[0];
      
      // Use NMP Plan number if available
      if (firstFeature?.properties?.PlanId) {
        return `NMP Plan ${firstFeature.properties.PlanId}`;
      }
      
      // Fallback to date-based name
      return `Imported Farm ${new Date().toLocaleDateString()}`;
    },
  },
};
</script>
